/**
 * Inbox / My Bookings pagination helpers.
 *
 * Bookings use API `perPage` + URL `page`. Upcoming/Past and multi-listing
 * filters are only partly expressible in Sharetribe `transactions.query`, so
 * we optionally walk API pages and accumulate matching rows until the current
 * UI page is filled — without downloading the full history in one shot.
 */

export const INBOX_PAGE_SIZE = 10;
export const INBOX_LISTING_FILTER_PAGE_SIZE = 20;
/** API batch size while accumulating filtered matches for one UI page. */
export const INBOX_API_FETCH_PAGE_SIZE = 10;
/** Safety cap so a sparse filter cannot scan unbounded history. */
export const INBOX_MAX_API_PAGES_PER_REQUEST = 25;

export const WHEN_FILTERS = {
  ALL: 'all',
  UPCOMING: 'upcoming',
  PAST: 'past',
};

export const getValidWhenFilter = when => {
  if (when === WHEN_FILTERS.PAST) {
    return WHEN_FILTERS.PAST;
  }
  if (when === WHEN_FILTERS.UPCOMING) {
    return WHEN_FILTERS.UPCOMING;
  }
  return WHEN_FILTERS.ALL;
};

export const parseListingIds = listingsParam => {
  if (!listingsParam) {
    return [];
  }
  if (Array.isArray(listingsParam)) {
    const first = listingsParam.filter(Boolean)[0];
    return first ? [first] : [];
  }
  const first = String(listingsParam)
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)[0];
  return first ? [first] : [];
};

export const parseInboxPage = page => {
  const n = Number(page);
  return Number.isFinite(n) && n > 1 ? Math.floor(n) : 1;
};

/**
 * Booking start from a denormalised or sparse transaction entity.
 * @param {Object} tx
 * @returns {Date|null}
 */
export const getBookingStartDate = tx => {
  const start = tx?.booking?.attributes?.displayStart || tx?.booking?.attributes?.start;
  const startDate = start instanceof Date ? start : start ? new Date(start) : null;
  return startDate && !Number.isNaN(startDate.getTime()) ? startDate : null;
};

/**
 * Upcoming = not concluded and booking start in the future; everything else is past.
 * @param {Object} params
 * @param {Object} params.tx
 * @param {boolean} [params.isFinal]
 * @param {Date|number} [params.now]
 * @returns {'upcoming'|'past'}
 */
export const getTransactionWhenBucket = ({ tx, isFinal = false, now = Date.now() }) => {
  const bookingStart = getBookingStartDate(tx);
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  return !isFinal && bookingStart && bookingStart.getTime() >= nowMs
    ? WHEN_FILTERS.UPCOMING
    : WHEN_FILTERS.PAST;
};

/**
 * Sharetribe `transactions.query` extras for when/listing filters.
 *
 * - Upcoming: `hasBooking` + `bookingStart` range (server-side).
 * - Past: no booking-date filter (past also includes non-booking txs and
 *   concluded bookings); client accumulate still applies.
 * - Single listing id: `listingId` (server-side). Multiple ids: client filter.
 *
 * @param {Object} params
 * @param {string} params.when
 * @param {string[]} [params.listingIds]
 * @param {Date} [params.now]
 * @returns {Object}
 */
export const buildInboxTransactionApiFilters = ({ when, listingIds = [], now = new Date() }) => {
  const validWhen = getValidWhenFilter(when);
  const isoNow = (now instanceof Date ? now : new Date(now)).toISOString();
  const filters = {};

  if (validWhen === WHEN_FILTERS.UPCOMING) {
    filters.hasBooking = true;
    filters.bookingStart = `${isoNow},`;
    // Ascending: soonest upcoming first (SDK default sort direction is desc).
    filters.sort = '-bookingStart';
  }

  if (listingIds.length === 1) {
    filters.listingId = listingIds[0];
  }

  return filters;
};

/**
 * @param {Object} tx
 * @param {string[]} listingIds
 * @returns {boolean}
 */
export const matchesInboxListingFilter = (tx, listingIds = []) => {
  if (!listingIds.length) {
    return true;
  }
  const listingId = tx?.listing?.id?.uuid || tx?.relationships?.listing?.data?.id?.uuid;
  return !!listingId && listingIds.includes(listingId);
};

/**
 * @param {Object} params
 * @param {Object} params.tx
 * @param {string} params.when
 * @param {boolean} [params.isFinal]
 * @param {Date|number} [params.now]
 * @returns {boolean}
 */
export const matchesInboxWhenFilter = ({ tx, when, isFinal = false, now = Date.now() }) => {
  const validWhen = getValidWhenFilter(when);
  if (validWhen === WHEN_FILTERS.ALL) {
    return true;
  }
  return getTransactionWhenBucket({ tx, isFinal, now }) === validWhen;
};

/**
 * Build PaginationLinks-compatible meta when exact total is unknown.
 * If `hasMore`, expose at least `page + 1` so Next works; otherwise clamp to `page`.
 *
 * @param {Object} params
 * @param {number} params.page
 * @param {number} params.itemCountOnPage
 * @param {boolean} params.hasMore
 * @param {number} [params.pageSize]
 * @returns {{ page: number, perPage: number, totalItems: number, totalPages: number }}
 */
export const buildInboxPaginationMeta = ({
  page = 1,
  itemCountOnPage = 0,
  hasMore = false,
  pageSize = INBOX_PAGE_SIZE,
}) => {
  const perPage = pageSize > 0 ? pageSize : INBOX_PAGE_SIZE;
  const safePage = Math.max(1, Number(page) || 1);
  const count = Math.max(0, Number(itemCountOnPage) || 0);

  if (hasMore) {
    return {
      page: safePage,
      perPage,
      totalItems: safePage * perPage + 1,
      totalPages: safePage + 1,
    };
  }

  const totalItems = (safePage - 1) * perPage + count;
  return {
    page: safePage,
    perPage,
    totalItems,
    totalPages: Math.max(1, safePage),
  };
};

/**
 * Decide whether Inbox pagination controls should render.
 *
 * @param {Object} params
 * @param {boolean} params.fetchInProgress
 * @param {{ totalPages?: number, page?: number }|null} params.pagination
 * @returns {boolean}
 */
export const shouldShowInboxPagination = ({ fetchInProgress, pagination }) => {
  if (fetchInProgress || !pagination) {
    return false;
  }
  return pagination.totalPages > 1 || pagination.page > 1;
};

/**
 * Walk API pages and accumulate rows matching `matches` until the UI page is
 * filled (plus one sentinel to detect another page). Stops early — does not
 * download the entire history.
 *
 * @param {Object} params
 * @param {(apiPage: number) => Promise<Object>} params.queryPage
 * @param {(entity: Object) => boolean} params.matches
 * @param {number} [params.uiPage]
 * @param {number} [params.pageSize]
 * @param {number} [params.maxApiPages]
 * @returns {Promise<{
 *   pageItems: Object[],
 *   hasMore: boolean,
 *   included: Object[],
 *   apiPagesFetched: number,
 *   matchedBeforePage: number,
 * }>}
 */
export const accumulateFilteredInboxPage = async ({
  queryPage,
  matches,
  uiPage = 1,
  pageSize = INBOX_PAGE_SIZE,
  maxApiPages = INBOX_MAX_API_PAGES_PER_REQUEST,
}) => {
  const safePage = Math.max(1, Number(uiPage) || 1);
  const perPage = pageSize > 0 ? pageSize : INBOX_PAGE_SIZE;
  const skip = (safePage - 1) * perPage;
  const needMatched = skip + perPage + 1;

  const matched = [];
  const included = [];
  let apiPage = 1;
  let apiTotalPages = 1;
  let apiPagesFetched = 0;

  while (
    matched.length < needMatched &&
    apiPage <= apiTotalPages &&
    apiPagesFetched < maxApiPages
  ) {
    const response = await queryPage(apiPage);
    apiPagesFetched += 1;
    apiTotalPages = Math.max(1, Number(response?.data?.meta?.totalPages) || 1);
    const pageData = response?.data?.data || [];
    const pageIncluded = response?.data?.included || [];
    included.push(...pageIncluded);

    pageData.forEach(entity => {
      if (matches(entity)) {
        matched.push(entity);
      }
    });

    if (pageData.length === 0) {
      break;
    }
    apiPage += 1;
  }

  const pageItems = matched.slice(skip, skip + perPage);
  const moreApiPagesRemain = apiPage <= apiTotalPages;
  const hitSafetyCap = apiPagesFetched >= maxApiPages && moreApiPagesRemain;
  const hasMore = matched.length > skip + perPage || hitSafetyCap;

  return {
    pageItems,
    hasMore,
    included,
    apiPagesFetched,
    matchedBeforePage: Math.min(skip, matched.length),
  };
};
